/**
 * Password Validation Utilities
 * Client-side password strength validation
 */

/**
 * Validate password strength
 * Requirements: 8+ characters, 1 uppercase, 1 special character
 * @param {string} password - Password to validate
 * @returns {object} - { valid: boolean, errors: string[], strength: string }
 */
export function validatePassword(password) {
  const errors = [];
  const checks = {
    length: false,
    uppercase: false,
    special: false
  };
  
  if (!password) {
    return {
      valid: false,
      errors: ['Password is required'],
      strength: 'none',
      checks
    };
  }
  
  // Check length
  if (password.length >= 8) {
    checks.length = true;
  } else {
    errors.push('Password must be at least 8 characters long');
  }
  
  // Check uppercase
  if (/[A-Z]/.test(password)) {
    checks.uppercase = true;
  } else {
    errors.push('Password must contain at least one uppercase letter');
  }
  
  // Check special character
  if (/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
    checks.special = true;
  } else {
    errors.push('Password must contain at least one special character');
  }
  
  // Calculate strength
  let strength = 'weak';
  const passedChecks = Object.values(checks).filter(Boolean).length;
  
  if (passedChecks === 3) {
    // All requirements met - check additional criteria for strength
    if (password.length >= 12 && /[0-9]/.test(password) && /[a-z]/.test(password)) {
      strength = 'strong';
    } else if (password.length >= 10) {
      strength = 'good';
    } else {
      strength = 'fair';
    }
  } else if (passedChecks === 2) {
    strength = 'weak';
  } else {
    strength = 'very-weak';
  }
  
  return {
    valid: errors.length === 0,
    errors,
    strength,
    checks
  };
}

/**
 * Get strength color for UI display
 * @param {string} strength - Strength level
 * @returns {string} - Tailwind color class
 */
export function getStrengthColor(strength) {
  switch (strength) {
    case 'strong':
      return 'bg-green-500';
    case 'good':
      return 'bg-blue-500';
    case 'fair':
      return 'bg-yellow-500';
    case 'weak':
      return 'bg-orange-500';
    case 'very-weak':
      return 'bg-red-500';
    default:
      return 'bg-gray-300';
  }
}

/**
 * Get strength label for UI display
 * @param {string} strength - Strength level
 * @returns {string} - Human-readable label
 */
export function getStrengthLabel(strength) {
  switch (strength) {
    case 'strong':
      return 'Strong';
    case 'good':
      return 'Good';
    case 'fair':
      return 'Fair';
    case 'weak':
      return 'Weak';
    case 'very-weak':
      return 'Very Weak';
    default:
      return '';
  }
}


